import { ArrowRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import useContactForm from "@/hooks/useContactForm";
import { useT } from "@/lib/i18n";

const FieldDark = ({ id, label, value, onChange, type = "text", testId }) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={id} className="text-[11px] tracking-[0.25em] uppercase text-charcoal/55">{label}</label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="bg-transparent border-b border-charcoal/20 py-3 text-[15px] text-charcoal focus:outline-none focus:border-terracotta transition-colors"
    />
  </div>
);

const SesionForm = () => {
  const t = useT();
  const { form, update, submit, loading, sent } = useContactForm({
    errorMissing: t("free.form.errorMissing"),
    errorSend: t("free.form.errorSend"),
    successMsg: t("free.form.success"),
  });

  return (
    <section id="formulario" className="py-20 md:py-28 bg-sand/30 scroll-mt-24">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <Reveal className="lg:col-span-5">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">{t("free.form.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-charcoal">
            {t("free.form.title1")} <span className="italic text-terracotta">{t("free.form.title2")}</span>?
          </h2>
          <p className="mt-6 text-charcoal/70 leading-relaxed">
            {t("free.form.lead")}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="bg-ivory text-charcoal border border-sand/70 rounded-3xl p-8 md:p-10"
            data-testid="free-session-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <FieldDark id="fs-name" label={t("free.form.name")} value={form.name} onChange={update("name")} testId="fs-name" />
              <FieldDark id="fs-email" label={t("free.form.email")} type="email" value={form.email} onChange={update("email")} testId="fs-email" />
            </div>
            <div className="mt-6">
              <FieldDark id="fs-phone" label={t("free.form.phone")} value={form.phone} onChange={update("phone")} testId="fs-phone" />
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <label htmlFor="fs-msg" className="text-[11px] tracking-[0.25em] uppercase text-charcoal/55">
                {t("free.form.msgLabel")}
              </label>
              <textarea
                id="fs-msg"
                rows={4}
                value={form.message}
                onChange={update("message")}
                data-testid="fs-message"
                placeholder={t("free.form.msgPlaceholder")}
                className="bg-transparent border-b border-charcoal/20 py-3 text-[15px] text-charcoal placeholder-charcoal/35 focus:outline-none focus:border-terracotta transition-colors resize-none"
              />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between">
              <p className="text-[12px] text-charcoal/55 max-w-xs leading-relaxed">
                {t("free.form.privacy")}
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="fs-submit"
                className="inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-8 py-4 text-sm font-medium hover:bg-terracotta transition-all disabled:opacity-60"
              >
                {loading ? t("free.form.sending") : sent ? t("free.form.sent") : t("free.form.submit")}
                {!loading && !sent && <ArrowRight size={15} />}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default SesionForm;
